import React from "react";
import DrinkCup from "../../../assets/images/drinkCup.svg";
import infoIcon from "../../../assets/images/info.png";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

export type Drinker = "heavy" | "medium" | "light";

type DrinkRowProps = {
    value: Drinker;
    onChange: (v: Drinker) => void;
    estDrinks: number | "";
    onEstDrinksChange: (v: number | "") => void;
    className?: string;
    labels?: { drinker?: string; estDrinks?: string };
    disableEstDrink?: boolean;
    hideDrinkerLabel?: boolean;
};

const DrinkRow: React.FC<DrinkRowProps> = ({
    value,
    onChange,
    estDrinks,
    onEstDrinksChange,
    className = "",
    labels = { drinker: "Drinking Pace", estDrinks: "Est Drinks" },
    disableEstDrink,
    hideDrinkerLabel,
}) => {
    const options: { id: Drinker; label: string; glasses: number; message: string }[] = [
        {
            id: 'light',
            label: 'Light',
            glasses: 1,
            message:
                '1 cocktail per person per hour. Best for: corporate events, daytime parties, family-friendly gatherings, wine/beer-heavy crowds.',
        },
        {
            id: 'medium',
            label: 'Standard',
            glasses: 2,
            message:
                '2 cocktails in the first hour, then 1 per hour after. Best for: birthdays, weddings, most celebrations (people start strong, then settle in).',
        },
        {
            id: 'heavy',
            label: 'Party',
            glasses: 3,
            message:
                '2 cocktails per person per hour. Best for: late-night parties, bachelor/bachelorette, big "let\'s rage" crowds.',
        },
    ];

    // Selected tab color — change these three classes to re-theme
    const activeClasses = "bg-orange-50 text-orange-600 ring-orange-300";
    const activeDotRing = "ring-orange-500";
    const activeDotFill = "bg-orange-500";

    // Cup tinted via CSS mask (orange when selected, gray otherwise)
    const Cup = ({ active }: { active: boolean }) => (
        <span
            className="inline-block h-4 w-3"
            style={{
                WebkitMask: `url(${DrinkCup}) no-repeat center / contain`,
                mask: `url(${DrinkCup}) no-repeat center / contain`,
                backgroundColor: active ? "#f97316" : "#6b7280", // orange-500 / gray-600
            }}
            aria-hidden
        />
    );

    return (
        <div
            className={`flex flex-col sm:flex-row sm:items-start sm:gap-8 min-w-0 ${hideDrinkerLabel ? "sm:justify-end" : ""} ${className}`}
        >
            {/* Left: Drinker pills — horizontal scroll on narrow screens so pills are not clipped */}
            <div className={hideDrinkerLabel ? "w-auto min-w-0 max-w-full" : "w-full min-w-0 sm:w-5/6"}>
                {!hideDrinkerLabel && <p className="font-semibold text-xs mb-2 text-[#19191C] pl-2">
                    {labels.drinker}
                </p>}
                <div className="overflow-x-auto overflow-y-hidden pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] sm:pb-0">
                    <div
                        role="radiogroup"
                        className={`flex flex-row flex-nowrap gap-3 w-max min-w-full sm:w-full sm:min-w-0 sm:gap-4 ${hideDrinkerLabel ? "" : "sm:justify-between"}`}
                    >
                    {options.map((opt) => {
                        const selected = value === opt.id;
                        return (
                            <label
                                key={opt.id}
                                className={[
                                    "flex flex-row shrink-0 justify-between items-center cursor-pointer rounded-full px-4 py-2 h-[35px]",
                                    hideDrinkerLabel ? "w-auto" : "w-auto min-w-[6.5rem] sm:min-w-0 sm:flex-1 sm:basis-0",
                                    " transition-colors",
                                    selected
                                        ? activeClasses
                                        : "bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100",
                                ].join(" ")}
                            >
                                {/* a11y radio (keeps label click behavior) */}
                                <input
                                    type="radio"
                                    name="drinker-type"
                                    value={opt.id}
                                    checked={selected}
                                    onChange={() => onChange(opt.id)}
                                    className="sr-only"
                                />
                                <span className="flex items-center">
                                    <span className="flex items-center mr-1">
                                        {Array.from({ length: opt.glasses }).map((_, i) => (
                                            <Cup key={i} active={selected} />
                                        ))}
                                    </span>
                                    <span className="text-xs font-medium">{opt.label}</span>
                                </span>

                                {/* Info popover — only opens via (i) click */}
                                <Popover className="relative mt-1 ml-2">
                                    <PopoverButton
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        className="outline-none"
                                        aria-label="More info"
                                    >
                                        <img src={infoIcon} className="w-3 h-3" alt="" />
                                    </PopoverButton>

                                    <PopoverPanel
                                        anchor="bottom"
                                        className="z-50 mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg"
                                        // prevent clicks inside panel from toggling the pill
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {opt.message}
                                    </PopoverPanel>
                                </Popover>
                            </label>
                        );
                    })}
                    </div>
                </div>
            </div>

            {/* Right: Est Drinks input */}
            {!hideDrinkerLabel && <div className={`w-full sm:w-1/3`}>
                <p className="font-semibold text-xs mb-2 text-gray-700 pl-2">
                    {labels.estDrinks}
                </p>
                <input
                    disabled={disableEstDrink}
                    type="text"
                    maxLength={21}
                    className="w-full px-4 py-3 font-[400] text-[14px] bg-p-gray rounded-full focus:outline-none  focus:ring-1 focus:ring-primary-red text-[#8D8D8D]"
                    placeholder="Est Drinks"
                    value={estDrinks}
                    onChange={(e) => {
                        const v = e.target.value;
                        onEstDrinksChange(v === "" ? "" : Number(v));
                    }}
                />
            </div>}
        </div>
    );
};

export default DrinkRow;
